import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { OfferService } from '../../../service/offer.service';
import { GraphqlService } from '../../../service/graphql.service';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import {
  Observable,
  map,
  of,
  Subscription,
  forkJoin,
  switchMap,
  catchError,
} from 'rxjs';
import { Category } from '../../../model/category';
import { OfferType } from '../../../model/offer-type';
import { User } from '../../../model/user';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { OfferWithImage } from '@/model/offer-with-image';
import { UserService } from '@/service/user.service';
import { Feedback, FeedbackResponse } from '@/model/feedback';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { FeedbackDetailsComponent } from '@/app/components/feedback-details/feedback-details.component';
import * as moment from 'moment';
import { MaterialDateModule } from '@/app/shared/material-date.module';
import { Location, LocationHierarchy } from '@/model/locations';
import { DataService } from '@/service/data.service';
import { OfferLocationSelectorComponent } from '@/app/components/offer-location-selector/offer-location-selector.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { KeycloakService } from 'keycloak-angular';
import { CompanyUser } from '@/model/company';
interface LoadedData {
  offerTypes: OfferType[];
  user: User;
  categories: Category[];
  offer?: OfferWithImage;
  history?: FeedbackResponse[];
}

@Component({
  selector: 'app-add-offer',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    NgxEditorModule,
    AsyncPipe,
    RouterLink,
    MatChipsModule,
    MatIconModule,
    MatDatepickerModule,
    CommonModule,
    MatCardModule,
    MatDividerModule,
    FeedbackDetailsComponent,
    MaterialDateModule,
    OfferLocationSelectorComponent,
    MatTooltipModule,
  ],
  templateUrl: './add-offer.component.html',
  styleUrls: ['./add-offer.component.scss'],
})
export class AddOfferComponent implements OnInit, OnDestroy {
  isEditMode = false;
  offerId: string | null = null;
  subscription: Subscription | null = null;
  isSubmitting = false;
  latestModificationFeedback: Feedback | null = null;
  showModificationFeedback = false;
  editModeFileName: string | null = null;
  allIndexableLocations: LocationHierarchy | null = null;
  userIsAdmin = false;
  companies$: Observable<CompanyUser[]> = of([]);

  angebotForm: FormGroup = this.createInitialForm();
  selectedFile: File | null = null;
  categories: Category[] = [];
  availableCategories: Observable<Category[]> = of([]);
  selectedFileUrl: string | null = null;
  minDate = new Date();
  offer: OfferWithImage | null = null;
  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    return date >= new Date(new Date().setHours(0, 0, 0, 0));
  };

  html = '';
  editor!: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h2', 'h3', 'h4'] }],
    ['link'],
    ['text_color'],
  ];

  offerTypes: OfferType[] = [];
  selectedOfferType: OfferType | null = null;
  acceptedMimeTypes: string | null = null;

  constructor(
    private fb: FormBuilder,
    private offerService: OfferService,
    private graphqlService: GraphqlService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private dataService: DataService,
    private keycloakService: KeycloakService,
  ) {
    this.initForm();
  }

  private createInitialForm(): FormGroup {
    return this.fb.group({
      titel: ['', [Validators.required, Validators.minLength(3)]],
      beschreibung: ['', [Validators.required, Validators.minLength(10)]],
      companyId: ['', Validators.required],
      foto: [null],
      videoUrl: [{ value: '', disabled: true }],
      categories: [[], [Validators.required, Validators.minLength(1)]],
      offerType: ['', Validators.required],
      appointmentDateTime: [{ value: null, disabled: true }],
      appointmentTime: [{ value: null, disabled: true }],
      locations: [
        { allAustria: true, selectedStates: [] },
        this.locationsValidator(),
      ],
    });
  }

  private locationsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      console.log('Locations value:', value);

      // If no value, return required error
      if (!value) {
        return { required: true };
      }

      // If allAustria is true, validation passes
      if (value.allAustria === true) {
        return null;
      }

      // If allAustria is false, we need at least one state
      if (
        value.allAustria === false &&
        (!value.selectedStates || value.selectedStates.length === 0)
      ) {
        return { stateRequired: true };
      }

      return null;
    };
  }

  private initForm(): void {
    // Subscribe to offerType changes
    const offerTypeControl = this.angebotForm.get('offerType');
    if (offerTypeControl) {
      offerTypeControl.valueChanges.subscribe((typeId) => {
        this.handleOfferTypeChange(typeId);
      });
    }
  }

  private handleOfferTypeChange(typeId: string): void {
    const selectedType = this.offerTypes.find((type) => type.id === typeId);
    this.selectedOfferType = selectedType || null;

    // Reset all media-related fields
    this.selectedFile = null;
    this.selectedFileUrl = null;
    const fotoControl = this.angebotForm.get('foto');
    const videoUrlControl = this.angebotForm.get('videoUrl');
    fotoControl?.setValue(null);
    videoUrlControl?.setValue('');

    if (selectedType) {
      this.acceptedMimeTypes = selectedType.acceptedMimeType;
      const dateTimeControl = this.angebotForm.get('appointmentDateTime');
      const timeControl = this.angebotForm.get('appointmentTime');

      // Handle date/time controls
      if (selectedType.requiresDate) {
        dateTimeControl?.enable();
        timeControl?.enable();
        dateTimeControl?.setValidators([Validators.required]);
      } else {
        dateTimeControl?.disable();
        timeControl?.disable();
        dateTimeControl?.clearValidators();
        timeControl?.clearValidators();
        dateTimeControl?.setValue(null);
        timeControl?.setValue(null);
      }
      dateTimeControl?.updateValueAndValidity();
      timeControl?.updateValueAndValidity();

      // Handle video URL and file upload controls
      if (this.acceptedMimeTypes === 'video/*') {
        videoUrlControl?.enable();
        videoUrlControl?.setValidators([
          Validators.required,
          Validators.pattern(
            /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|vimeo\.com\/)[\w-]+/,
          ),
        ]);
        fotoControl?.disable();
        fotoControl?.clearValidators();
      } else {
        videoUrlControl?.disable();
        videoUrlControl?.clearValidators();
        fotoControl?.enable();
        fotoControl?.setValidators([Validators.required]);
      }
      videoUrlControl?.updateValueAndValidity();
      fotoControl?.updateValueAndValidity();
    }

    // Subscribe to form value changes
    this.angebotForm.valueChanges.subscribe(() => {
      if (this.angebotForm.errors) {
        console.log('Form-level validation errors:', this.angebotForm.errors);
      }

      Object.keys(this.angebotForm.controls).forEach((key) => {
        const control = this.angebotForm.get(key);
        if (control?.errors) {
          console.log(`Validation errors for ${key}:`, control.errors);
        }
      });
    });
  }

  ngOnInit() {
    this.editor = new Editor();
    // Determine admin role and load companies if admin
    this.userIsAdmin = this.keycloakService.getUserRoles().includes('admin');
    if (this.userIsAdmin) {
      this.companies$ = this.userService.getAllCompanyInvitations().pipe(
        map((companies) =>
          [...companies]
            .filter((company) => company.companyName !== null)
            .sort((a, b) =>
              (a.companyName || '').localeCompare(b.companyName || ''),
            ),
        ),
        catchError(() => of([] as CompanyUser[])),
      );
      // Ensure companyId remains enabled for admin selection
      this.angebotForm.get('companyId')?.enable();
    } else {
      // Non-admin should not change company; keep control disabled after value set
      this.angebotForm.get('companyId')?.disable();
    }
    this.loadInitialData();
  }

  private loadInitialData(): void {
    this.subscription = this.route.params
      .pipe(
        map((params) => {
          this.offerId = params['id'];
          this.isEditMode = !!this.offerId;
          return this.isEditMode;
        }),
        switchMap((isEditMode) => {
          const offerTypes$ = this.offerService.getAllOfferTypes();
          const userInfo$ = this.userService.getUserInfo();
          const categories$ = this.graphqlService.allCategories();

          // Only fetch offer and history if we have an ID and are in edit mode
          if (isEditMode && this.offerId) {
            const offer$ = this.offerService.getOfferById(this.offerId).pipe(
              catchError((error) => {
                console.error('Error fetching offer:', error);
                this.showError('Dieses Angebot ist nicht verfügbar.');
                this.router.navigate(['/home']);
                return of(null);
              }),
            );
            const history$ = this.offerService.getWorkflowHistory(this.offerId);
            return forkJoin({
              offerTypes: offerTypes$,
              user: userInfo$,
              categories: categories$,
              offer: offer$,
              history: history$,
            });
          }

          // Create mode - don't fetch offer or history
          return forkJoin({
            offerTypes: offerTypes$,
            user: userInfo$,
            categories: categories$,
          });
        }),
      )
      .subscribe({
        next: (results: LoadedData) => {
          const { offerTypes, user, categories, offer, history } = results;
          this.offer = offer || null;

          // If we're in edit mode but no offer was found, stop here
          if (this.isEditMode && !offer) {
            return;
          }

          this.handleLoadedData(offerTypes, user, categories, offer);

          if (history) {
            this.handleHistoryData(history);
          }
        },
        error: (error) => {
          console.error('Error loading initial data:', error);
          this.showError('Fehler beim Laden der Daten');
          this.router.navigate(['/home']);
        },
      });

    this.dataService.getLocations().subscribe((locations) => {
      this.allIndexableLocations = locations;
    });
  }

  private handleLineBreaks(content: string): string {
    // Replace newlines with <br/> tags
    return content
      .replace(/\n/g, '<br/>')
      .replace(/<\/p><p>/g, '</p><br/><p>')
      .replace(/<\/div><div>/g, '</div><br/><div>');
  }

  private revertLineBreaks(content: string): string {
    // Convert <br/>, <br>, <br /> back to newlines
    return content
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/<\/p><p>/g, '\n')
      .replace(/<\/div><div>/g, '\n');
  }

  private handleLoadedData(
    offerTypes: OfferType[],
    user: User,
    categories: Category[],
    offer?: OfferWithImage,
  ): void {
    // Handle offer types
    this.offerTypes = offerTypes;

    // Handle user info
    this.angebotForm
      .get('companyId')
      ?.setValue(offer?.companyId || user.company?.id);
    if (!this.userIsAdmin) {
      this.angebotForm.get('companyId')?.disable();
    }

    // Handle categories
    this.categories = categories.filter(
      (category: Category) => category.parentId === null,
    );
    this.availableCategories = of(this.categories);

    // Handle offer data if in edit mode
    if (offer) {
      if (offer.status === 'REJECTED') {
        this.showError(
          'Dieses Angebot wurde abgelehnt und kann nicht bearbeitet werden.',
        );
        this.router.navigate(['/company']);
        return;
      }

      if (
        !this.offerService.userIsAssociatedWithOffer(
          offer,
          this.userService.getUser()!,
        )
      ) {
        this.showError(
          'Sie sind nicht mit diesem Angebot verbunden und können es nicht bearbeiten.',
        );
        this.router.navigate(['/company']);
        return;
      }

      const appointmentTime = `${offer.appointmentTimeHours
        .toString()
        .padStart(2, '0')}:${offer.appointmentTimeMinutes
        .toString()
        .padStart(2, '0')}`;

      // Convert <br/> tags back to newlines in the description
      const description = this.revertLineBreaks(offer.description);

      // Prepare location data
      const locationValue = {
        allAustria: false,
        selectedStates: offer.bundeslaender?.map((state) => state.id) || [],
        selectedDistricts: offer.bezirke?.map((district) => district.id) || [],
        selectedLocations: offer.orte || [],
      };

      this.angebotForm.patchValue({
        titel: offer.name,
        beschreibung: description,
        categories: offer.categories.map((c: Category) => c.id),
        offerType: offer.offerType?.id,
        appointmentDateTime: offer.appointmentDateTime
          ? moment.parseZone(offer.appointmentDateTime).startOf('day')
          : null,
        appointmentTime: appointmentTime,
        foto: offer.imageUrl,
        companyId: offer.companyId,
        offerId: offer.id,
        videoUrl: offer.videoEmbeddedLink,
        locations: locationValue,
      });

      this.selectedFileUrl = offer.imageUrl;
      this.angebotForm.get('foto')?.setValue(offer.imageUrl);

      // Extract filename from URL for PDF files
      if (
        offer.offerType?.acceptedMimeType === 'application/pdf' &&
        offer.imageUrl
      ) {
        this.editModeFileName = this.extractFilenameFromUrl(offer.imageUrl);
      }

      // Remove required validator for image in edit mode
      const fotoControl = this.angebotForm.get('foto');
      if (fotoControl) {
        fotoControl.clearValidators();
        fotoControl.updateValueAndValidity();
      }
    }
  }

  private extractFilenameFromUrl(url: string): string {
    try {
      const urlParts = url.split('/');
      let filename = urlParts[urlParts.length - 1];
      // Remove query parameters if they exist
      filename = filename.split('?')[0];
      // URL decode the filename
      return decodeURIComponent(filename);
    } catch {
      return 'document.pdf';
    }
  }

  private handleHistoryData(history: FeedbackResponse[]): void {
    if (!history || history.length === 0) return;

    const feedbackResponse = history.map((item) => {
      return new FeedbackResponse(item);
    });

    // Sort history by date descending and find latest REQUEST_MODIFICATION
    const sortedHistory = [...feedbackResponse].sort(
      (a, b) =>
        new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime(),
    );

    const latestModification = sortedHistory.find(
      (item) => item.newStatus === 'REQUEST_MODIFICATION',
    );

    if (latestModification) {
      this.latestModificationFeedback = {
        ...latestModification.convertedFeedback,
      };
    }
  }

  toggleModificationFeedback(): void {
    this.showModificationFeedback = !this.showModificationFeedback;
  }

  onFileChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      if (!this.isFileTypeAccepted(file)) {
        this.showError('Dieser Dateityp wird nicht unterstützt.');
        return;
      }

      this.selectedFile = file;
      this.angebotForm.get('foto')?.setValue(file);
      const reader = new FileReader();
      reader.onload = (e: Event) => {
        this.selectedFileUrl = (e.target as FileReader).result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  submit(createAnother: boolean) {
    if (this.angebotForm.invalid) {
      this.angebotForm.markAllAsTouched();
      return;
    }

    const formData = this.prepareFormData();
    //  return;
    this.isSubmitting = true;

    if (this.isEditMode && this.offerId) {
      if (this.selectedFileUrl === formData.get('imageFile')) {
        formData.delete('imageFile');
      }
      this.updateOffer(formData);
    } else {
      this.createOffer(formData, createAnother);
    }
  }

  private prepareFormData(): FormData {
    const formData = new FormData();
    const formValue = this.angebotForm.value;

    // Handle line breaks in description
    const description = this.handleLineBreaks(formValue.beschreibung);

    formData.append('name', formValue.titel);
    formData.append('description', description);
    formData.append('offerTypeId', formValue.offerType);

    if (formValue.companyId) {
      formData.append('companyId', formValue.companyId);
    }

    formValue.categories.forEach((categoryId: string) =>
      formData.append('categoryIds', categoryId),
    );

    // Handle locations
    const locationValue = formValue.locations;
    if (locationValue) {
      if (locationValue.allAustria) {
        formData.append('allAustria', 'true');
      } else {
        formData.append('allAustria', 'false');
        if (locationValue.selectedLocations?.length) {
          formData.append(
            'orteIds',
            locationValue.selectedLocations
              .map((location: Location) => location.id)
              .join(','),
          );
        } else if (locationValue.selectedDistricts?.length) {
          formData.append(
            'bezirkeIds',
            locationValue.selectedDistricts.join(','),
          );
        } else if (locationValue.selectedStates?.length) {
          formData.append(
            'bundeslaenderIds',
            locationValue.selectedStates.join(','),
          );
        }
      }
    }

    if (this.selectedOfferType?.acceptedMimeType === 'video/*') {
      formData.append('videoEmbeddedLink', formValue.videoUrl);
    } else if (this.selectedFile) {
      formData.append('imageFile', this.selectedFile, this.selectedFile.name);
    }

    if (this.isEditMode && this.offerId) {
      formData.append('id', this.offerId);
    }

    // Handle appointment date/time if present
    if (this.selectedOfferType?.requiresDate) {
      const date = this.angebotForm.get('appointmentDateTime')?.value;
      const time = this.angebotForm.get('appointmentTime')?.value;

      if (date && date.isValid()) {
        // Create a new moment object preserving the input date exactly as entered
        let momentDate = moment.parseZone(date.format('YYYY-MM-DD'));

        if (time) {
          const [hours, minutes] = time.split(':');
          momentDate = momentDate
            .hours(parseInt(hours))
            .minutes(parseInt(minutes));
        } else {
          // If no time specified, set to start of day
          momentDate = momentDate.startOf('day');
        }

        // Format in ISO but preserve the local timezone
        formData.append('appointmentDateTime', momentDate.format());
      }
    }

    return formData;
  }

  private updateOffer(formData: FormData): void {
    this.offerService.editOfferWithImage(formData).subscribe({
      next: () => {
        this.showSuccess('Angebot erfolgreich aktualisiert!');
        this.isSubmitting = false;
        this.router.navigate(['/company']);
      },
      error: (error) => {
        console.error('Fehler beim Aktualisieren des Angebots', error);
        this.showError('Fehler beim Aktualisieren des Angebots.');
        this.isSubmitting = false;
      },
    });
  }

  private createOffer(formData: FormData, createAnother: boolean): void {
    this.offerService.addOfferWithImage(formData).subscribe({
      next: () => {
        this.showSuccess('Angebot erfolgreich hinzugefügt!');
        this.isSubmitting = false;
        if (createAnother) {
          this.resetForm();
        } else {
          this.router.navigate(['/company']);
        }
      },
      error: (error) => {
        console.error('Fehler beim Hinzufügen des Angebots', error);
        this.showError('Fehler beim Hinzufügen des Angebots.');
        this.isSubmitting = false;
      },
    });
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Schließen', {
      duration: 5000,
      panelClass: ['custom-snackbar', 'success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Schließen', {
      duration: 3000,
      panelClass: ['snackbar-error'],
    });
  }

  resetForm() {
    this.angebotForm.reset();
    // Handle user info
    this.angebotForm
      .get('companyId')
      ?.setValue(this.userService.getUser()?.company?.id);
    this.selectedFile = null;
    this.selectedFileUrl = null;
    const categoriesControl = this.angebotForm.get('categories');
    if (categoriesControl) {
      categoriesControl.setValue([]);
    }
  }

  ngOnDestroy(): void {
    this.editor.destroy();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  getTextForUploadField(): string {
    if (this.selectedOfferType?.acceptedMimeType == 'image/*') {
      return 'Bitte ein Bild auswählen.';
    } else if (this.selectedOfferType?.acceptedMimeType == 'video/*') {
      return 'Bitte ein Video auswählen.';
    } else if (this.selectedOfferType?.acceptedMimeType == 'application/pdf') {
      return 'Bitte ein PDF auswählen.';
    }
    return 'Dieser Angebotstyp erlaubt keinen Dateiupload.';
  }

  private isFileTypeAccepted(file: File): boolean {
    if (!this.acceptedMimeTypes) return true;

    const acceptedTypes = this.acceptedMimeTypes.split(',');
    return acceptedTypes.some((type) => {
      if (type.includes('/*')) {
        const baseType: string = type.split('/')[0];
        return file.type.startsWith(baseType + '/');
      }
      return type === file.type;
    });
  }

  getCategoryName(categoryId: string): string {
    return this.categories.find((c) => c.id === categoryId)?.name || '';
  }
}
